import { Button, Group, Loader, Menu, Table, Text } from '@mantine/core';
import { IconDots, IconDownload, IconPlus } from '@tabler/icons-react';
import { useMemo, useState } from 'react';
import { useCurrentClient } from 'src/clients/context/CurrentClientProvider';
import dataCaptureClasses from 'src/data-capture/DataCapture.module.css';
import { useAgenda } from 'src/meeting-agenda/AgendaProvider';
import { useDeleteAgenda } from 'src/suitability-report/hooks/useDeleteAgenda';
import { useMeetingAgenda } from 'src/suitability-report/hooks/useMeetingAgendas';
import { GenericErrorPage } from '../../error/GenericErrorPage';
import PageLoader from '../../UI/loader/PageLoader';
import { useFileTemporaryLinkMutation } from '../hooks/useFileTemporaryLinkMutation';
import SuitabilityReportStatusPill from './SuitabilityReportStatusPill';
import { notifyAndLogError } from 'src/UI/common/ui-helpers';
import FileUploadLayout from './FileUploadLayout';
import EmptyView from './components/EmptyView';
import FileNameWithIcon from './files-table/FileNameWithIcon';
import { dateMatchesSearch, formatDateTime } from 'src/meetings/utils/date-utils';

export default function MeetingAgendas() {
  const { meetingAgendas, isLoading, isError, error, refetch } = useMeetingAgenda();
  const { clientId } = useCurrentClient();
  const {
    createLink,
    isLoading: isCreatingLink,
    isError: creatingLinkFailed,
  } = useFileTemporaryLinkMutation();
  const { deleteMeetingAgenda } = useDeleteAgenda();
  const { open: openAgendaFlow } = useAgenda();
  const [searchValue, setSearchValue] = useState('');

  const handleDownload = async (agendaFileId: number) => {
    try {
      const link = await createLink(agendaFileId);
      window.open(link.url, '_blank');
    } catch (e) {
      notifyAndLogError(
        'Failed to download meeting agenda',
        'An error occurred while trying to download the meeting agenda. Please try again later.',
        e,
        { clientId, agendaFileId },
      );
      return;
    }
  };

  const handleDelete = async (reportId: string) => {
    try {
      await deleteMeetingAgenda(reportId);
    } catch (e) {
      notifyAndLogError(
        'Failed to delete meeting agenda',
        'An error occurred while trying to delete the meeting agenda. Please try again later.',
        e,
        { clientId, reportId },
      );
      return;
    }
    await refetch();
  };

  const filteredMeetingAgendas = useMemo(() => {
    if (!searchValue) return meetingAgendas;

    const searchLower = searchValue.toLowerCase();
    return meetingAgendas.filter(
      agenda =>
        (agenda.fileName || '').toLowerCase().includes(searchLower) ||
        dateMatchesSearch(agenda.createdAt, searchLower),
    );
  }, [meetingAgendas, searchValue]);

  const rows = useMemo(
    () =>
      filteredMeetingAgendas.map(agenda => (
        <Table.Tr key={agenda.id}>
          <Table.Td>
            <FileNameWithIcon
              fileName={agenda.fileName || 'No file'}
              isFolder={false}
              isGenerated={agenda.isGenerated}
            />
          </Table.Td>
          <Table.Td>
            <Text size="sm">{formatDateTime(agenda.createdAt)}</Text>
          </Table.Td>
          <Table.Td>
            <SuitabilityReportStatusPill status={agenda.backgroundProcess?.status ?? 'SUCCESS'} />
          </Table.Td>
          <Table.Td align="right">
            <Group justify="space-between" wrap="nowrap">
              {agenda.fileId ? (
                <Button
                  variant="primary"
                  onClick={() => handleDownload(agenda.fileId!)}
                  rightSection={<IconDownload size={16} />}
                >
                  Download
                </Button>
              ) : (
                <div />
              )}
              {agenda?.backgroundProcess?.status &&
                ['SUCCESS', 'FAILED'].includes(agenda?.backgroundProcess?.status) && (
                  <Menu
                    transitionProps={{ transition: 'pop-top-right' }}
                    position="bottom-end"
                    width={220}
                    withinPortal
                  >
                    <Menu.Target>
                      <IconDots color="#475467" stroke={2} size={20} />
                    </Menu.Target>

                    <Menu.Dropdown>
                      <Menu.Item onClick={() => handleDelete(agenda.id)}>Delete</Menu.Item>
                    </Menu.Dropdown>
                  </Menu>
                )}
            </Group>
          </Table.Td>
        </Table.Tr>
      )),
    [filteredMeetingAgendas],
  );

  if (isError || creatingLinkFailed) {
    return <GenericErrorPage error={error || { creatingLinkFailed }} />;
  }

  if (isCreatingLink) {
    return <PageLoader absolute={false} />;
  }

  if (filteredMeetingAgendas.length === 0 && !isLoading) {
    return (
      <FileUploadLayout title="Meeting Agendas">
        <EmptyView
          title="Get ready for your next meeting"
          subtitle="Generate tailored meeting agendas with Siesta AI to keep discussions focused and valuable."
          ctaText="Generate new"
          ctaIcon={IconPlus}
          onCtaClick={openAgendaFlow}
        />
      </FileUploadLayout>
    );
  }

  return (
    <FileUploadLayout
      title="Meeting Agendas"
      showSearch
      searchValue={searchValue}
      onSearchChange={setSearchValue}
      actions={
        <Button
          variant="primary"
          leftSection={<IconPlus size={16} />}
          size="md"
          onClick={openAgendaFlow}
        >
          Generate
        </Button>
      }
    >
      <Table
        variant="bare"
        captionSide="bottom"
        classNames={{ th: dataCaptureClasses.th }}
        verticalSpacing={'sm'}
      >
        <Table.Thead>
          <Table.Tr>
            <Table.Th>File name</Table.Th>
            <Table.Th>Created</Table.Th>
            <Table.Th>Status</Table.Th>
            <Table.Th></Table.Th>
          </Table.Tr>
        </Table.Thead>
        <Table.Tbody>
          {isLoading ? (
            <Table.Tr>
              <Table.Td colSpan={4} style={{ textAlign: 'center', padding: '2rem' }}>
                <Loader size="sm" />
              </Table.Td>
            </Table.Tr>
          ) : (
            rows
          )}
        </Table.Tbody>
      </Table>
    </FileUploadLayout>
  );
}
